import axios from "axios";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/users";

const api = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Get token from localStorage (blitzware stores it there)
  const token = localStorage.getItem("access_token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token is invalid or expired
      console.warn("Authentication token expired or invalid");

      // Clear stored tokens
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");

      // Redirect to login page
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

const useUser = () => {
  const getCurrentUser = async (userId) => {
    try {
      const response = await api.post("/me", { userId });
      return response.data;
    } catch (error) {
      console.error("Error fetching current user:", error);
      throw error;
    }
  };

  const getUserRole = async (userId) => {
    try {
      const response = await api.post("/me/role", { userId });
      return response.data;
    } catch (error) {
      console.error("Error fetching user role:", error);
      throw error;
    }
  };
  const updateUser = async (userData) => {
    try {
      const response = await api.put(`/${userData.id}/update`, userData);
      return response.data;
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  };

  const getAllUsers = async () => {
    try {
      const response = await api.get("/all");
      return response.data;
    } catch (error) {
      console.error("Error fetching all users:", error);
      throw error;
    }
  };

  const getUserPoints = async (userId) => {
    try {
      const response = await api.post("/me/points", { userId });
      return response.data.points;
    } catch (error) {
      console.error("Error fetching user points:", error);
      throw error;
    }
  };

  const addUser = async (userData) => {
    try {
      const response = await api.post("/add", userData);
      return response.data;
    } catch (error) {
      console.error("Error adding user:", error);
      throw error;
    }
  };

  const getPointsTransactions = async (userId) => {
    try {
      const response = await api.post("/me/points/transactions", { userId });
      return response.data;
    } catch (error) {
      console.error("Error fetching points transactions:", error);
      throw error;
    }
  };

  const getUserStats = async (userId) => {
    try {
      const [
        points,
        transactions,
        recommendations,
        activeSpots,
        totalEarnings,
      ] = await Promise.all([
        api.post("/me/points", { userId }),
        api.post("/me/points/transactions", { userId }),
        api.post("/me/recommendations", { userId }),
        api.post("/me/active-spots", { userId }),
        api.post("/me/total-earnings", { userId }),
      ]);

      console.log("totalEarnings", totalEarnings.data.earnings);
      console.log(
        "transactions",
        transactions,
        recommendations,
        activeSpots,
        totalEarnings,
        points
      );
      return {
        points: points.data.points,
        transactions: transactions.data.transactions,
        recommendationsCount: recommendations.data.count,
        likesCount: recommendations.data.likesCount,
        activeSpots: activeSpots.data.count,
        totalEarnings: totalEarnings.data.earnings,
      };
    } catch (error) {
      console.error("Error fetching user stats:", error);
      throw error;
    }
  };

  const updatePoints = async (points, type, referenceId, userId) => {
    try {
      const response = await api.post("/me/points/update", {
        points,
        type,
        referenceId,
        userId,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating points:", error);
      throw error;
    }
  };

  const approveRegistration = async (userId) => {
    try {
      const response = await api.post(`/${userId}/approve`, {
        status: 1,
        accepted: 1,
      });
      return response.data;
    } catch (error) {
      console.error("Error approving registration:", error);
      throw error;
    }
  };

  const rejectRegistration = async (userId) => {
    try {
      const response = await api.post(`/${userId}/reject`, {
        status: 0,
        accepted: 1,
      });
      return response.data;
    } catch (error) {
      console.error("Error rejecting registration:", error);
      throw error;
    }
  };

  // Update user onboarding preferences
  const updateOnboardingPreferences = async (
    buildingId,
    parkingSpotId,
    userId,
    externalAuthId,
    email,
    username
  ) => {
    try {
      const requestData = {
        buildingId,
        parkingSpotId,
        userId,
        externalAuthId,
        email,
        username,
      };
      console.log(
        "API - updateOnboardingPreferences - sending data:",
        requestData
      );

      const response = await api.post(`/onboarding`, requestData);
      return response.data;
    } catch (error) {
      console.error("Error updating onboarding preferences:", error);
      throw error;
    }
  };

  // Sync user with external auth
  const syncUserWithExternalAuth = async (externalUserData) => {
    try {
      const response = await api.post(`/sync`, externalUserData);
      return response.data;
    } catch (error) {
      console.error("Error syncing user with external auth:", error);
      throw error;
    }
  };

  return {
    getCurrentUser,
    getUserPoints,
    getUserRole,
    addUser,
    updateUser,
    getAllUsers,
    getPointsTransactions,
    updatePoints,
    approveRegistration,
    rejectRegistration,
    getUserStats,
    updateOnboardingPreferences,
    syncUserWithExternalAuth,
  };
};

export default useUser;
